import React, { useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, Tooltip,
} from 'recharts';
import { Star, ExternalLink, ChevronDown, ChevronUp } from 'lucide-react';
import { COMPETITORS, FEATURES, OUR_SOFTWARE, getScore } from './marketData';

const ScoreCell = ({ val }) => {
  if (val === 1)   return <div className="w-5 h-5 rounded-full bg-emerald-500 mx-auto" title="Full" />;
  if (val === 0.5) return <div className="w-5 h-5 rounded-full bg-amber-400 mx-auto flex items-center justify-center"><span className="text-white text-[9px] font-bold">½</span></div>;
  return <div className="w-5 h-5 rounded-full bg-slate-200 mx-auto" title="Missing" />;
};

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={10} className={i <= Math.round(rating) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'} />
    ))}
    <span className="text-xs text-slate-500 ml-1">{rating}</span>
  </div>
);

const PriorityBar = ({ score, color }) => (
  <div className="w-full bg-slate-100 rounded-full h-1.5 mt-1">
    <div className="h-1.5 rounded-full transition-all" style={{ width: `${score}%`, background: color }} />
  </div>
);

const RADAR_KEYS = ['client_mgmt','scheduling','staff_mgmt','ndis_billing','mobile','gps','incidents','financial','ai_features','compliance'];
const RADAR_LABELS = {
  client_mgmt: 'Clients', scheduling: 'Scheduling', staff_mgmt: 'Staff',
  ndis_billing: 'Billing', mobile: 'Mobile', gps: 'GPS',
  incidents: 'Incidents', financial: 'Finance', ai_features: 'AI', compliance: 'Compliance',
};

export default function CompetitorMatrix() {
  const [expanded, setExpanded] = useState(null);
  const [filterCat, setFilterCat] = useState('All');
  const categories = ['All', ...Array.from(new Set(FEATURES.map(f => f.category)))];

  const visibleFeatures = filterCat === 'All' ? FEATURES : FEATURES.filter(f => f.category === filterCat);

  const radarData = RADAR_KEYS.map(key => ({
    feature: RADAR_LABELS[key],
    Rostery: OUR_SOFTWARE.scores[key] * 100,
    ...COMPETITORS.reduce((obj, c) => { obj[c.name] = c.scores[key] * 100; return obj; }, {}),
  }));

  const ourScore = getScore(OUR_SOFTWARE.scores);

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Competitor Analysis Matrix</h1>
        <p className="text-sm text-slate-500 mt-1">
          10 Australian NDIS software competitors · 20 features compared · May 2026
        </p>
      </div>

      {/* Summary score cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-6 gap-3">
        {/* Our software card */}
        <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border-2 border-indigo-300 rounded-2xl p-3 flex flex-col items-center shadow-sm">
          <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center mb-1">
            <span className="text-white text-[10px] font-bold">US</span>
          </div>
          <p className="text-xs font-bold text-indigo-700 text-center leading-tight">{OUR_SOFTWARE.name}</p>
          <p className="text-lg font-black text-indigo-600 mt-1">{ourScore}%</p>
          <PriorityBar score={ourScore} color="#6366f1" />
        </div>

        {COMPETITORS.map((c) => {
          const score = getScore(c.scores);
          return (
            <div key={c.id} className="bg-white border border-slate-100 rounded-2xl p-3 flex flex-col items-center shadow-sm hover:shadow-md transition-shadow">
              <div className="w-8 h-8 rounded-full flex items-center justify-center mb-1" style={{ background: c.color + '20' }}>
                <span className="text-[10px] font-bold" style={{ color: c.color }}>{c.name.slice(0, 2)}</span>
              </div>
              <p className="text-xs font-semibold text-slate-600 text-center leading-tight">{c.name}</p>
              <p className="text-lg font-black mt-1" style={{ color: c.color }}>{score}%</p>
              <PriorityBar score={score} color={c.color} />
            </div>
          );
        })}
      </div>

      {/* Radar chart */}
      <div className="bg-white rounded-2xl border border-slate-100 p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-700 mb-1">Multi-dimensional Comparison (Core Features)</h2>
        <p className="text-xs text-slate-400 mb-4">Rostery vs top 4 competitors on key dimensions</p>
        <ResponsiveContainer width="100%" height={300}>
          <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
            <PolarGrid stroke="#e2e8f0" />
            <PolarAngleAxis dataKey="feature" tick={{ fontSize: 10, fill: '#64748b' }} />
            <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fontSize: 9, fill: '#94a3b8' }} tickCount={4} />
            <Radar name="Rostery" dataKey="Rostery" stroke="#6366f1" fill="#6366f1" fillOpacity={0.2} strokeWidth={2} />
            <Radar name="ShiftCare" dataKey="ShiftCare" stroke="#10b981" fill="#10b981" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
            <Radar name="Lumary"    dataKey="Lumary"    stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
            <Radar name="AlayaCare" dataKey="AlayaCare" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
            <Radar name="CareMaster" dataKey="CareMaster" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 2" />
            <Tooltip formatter={(v) => [`${v}%`]} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Feature matrix table */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="flex items-center justify-between p-4 border-b border-slate-100">
          <h2 className="text-sm font-semibold text-slate-700">Full Feature Comparison Matrix</h2>
          <div className="flex gap-2 flex-wrap">
            {categories.map(cat => (
              <button key={cat} onClick={() => setFilterCat(cat)}
                className={`text-xs px-3 py-1 rounded-full border transition-colors ${filterCat === cat ? 'bg-indigo-600 text-white border-indigo-600' : 'border-slate-200 text-slate-500 hover:bg-slate-50'}`}>
                {cat}
              </button>
            ))}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                <th className="text-left px-4 py-3 text-slate-500 font-medium w-44">Feature</th>
                <th className="px-2 py-3 text-center" style={{ color: '#6366f1', fontWeight: 700 }}>Rostery</th>
                {COMPETITORS.map(c => (
                  <th key={c.id} className="px-2 py-3 text-center" style={{ color: c.color, fontWeight: 600 }}>{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {visibleFeatures.map((f) => (
                <tr key={f.id} className="hover:bg-slate-50">
                  <td className="px-4 py-2.5 text-slate-600 font-medium">
                    <div>{f.label}</div>
                    <div className="text-[10px] text-slate-400">{f.category}</div>
                  </td>
                  <td className="px-2 py-2.5 text-center">
                    <ScoreCell val={OUR_SOFTWARE.scores[f.id]} />
                  </td>
                  {COMPETITORS.map(c => (
                    <td key={c.id} className="px-2 py-2.5 text-center">
                      <ScoreCell val={c.scores[f.id]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 border-t border-slate-200">
                <td className="px-4 py-2.5 text-xs font-bold text-slate-600">Overall Score</td>
                <td className="px-2 py-2.5 text-center font-black text-sm" style={{ color: '#6366f1' }}>{ourScore}%</td>
                {COMPETITORS.map(c => (
                  <td key={c.id} className="px-2 py-2.5 text-center font-bold text-sm" style={{ color: c.color }}>
                    {getScore(c.scores)}%
                  </td>
                ))}
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="flex items-center gap-4 px-4 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-500">
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-emerald-500" /> Full feature</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-amber-400" /> Partial / basic</div>
          <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-full bg-slate-200" /> Missing</div>
        </div>
      </div>

      {/* Competitor cards */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Competitor Profiles</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {COMPETITORS.map((c) => {
            const score = getScore(c.scores);
            const isOpen = expanded === c.id;
            return (
              <div key={c.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
                <button className="w-full text-left p-4" onClick={() => setExpanded(isOpen ? null : c.id)}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: c.color + '20' }}>
                        <span className="font-black text-sm" style={{ color: c.color }}>{c.name.slice(0,2)}</span>
                      </div>
                      <div>
                        <p className="font-bold text-slate-700 text-sm">{c.name}</p>
                        <p className="text-xs text-slate-400">{c.hq} · {c.focus}</p>
                        <Stars rating={c.rating} />
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div>
                        <p className="text-lg font-black text-right" style={{ color: c.color }}>{score}%</p>
                        <p className="text-[10px] text-slate-400 text-right">{c.pricing}</p>
                      </div>
                      {isOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                    </div>
                  </div>
                </button>
                {isOpen && (
                  <div className="px-4 pb-4 space-y-3 border-t border-slate-100 pt-3">
                    <p className="text-xs text-slate-500 leading-relaxed">{c.description}</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
                        <p className="text-xs font-semibold text-emerald-700 mb-1.5">Strengths</p>
                        <ul className="space-y-0.5">
                          {c.strengths.map((s, i) => (
                            <li key={i} className="text-xs text-emerald-600 flex items-start gap-1">
                              <span className="mt-0.5 shrink-0">+</span>{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div className="bg-red-50 border border-red-100 rounded-xl p-3">
                        <p className="text-xs font-semibold text-red-700 mb-1.5">Weaknesses</p>
                        <ul className="space-y-0.5">
                          {c.weaknesses.map((w, i) => (
                            <li key={i} className="text-xs text-red-600 flex items-start gap-1">
                              <span className="mt-0.5 shrink-0">−</span>{w}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-400">
                      <span>Founded {c.founded} · {c.employees} employees</span>
                      <span className="font-medium text-slate-500">{c.url}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
